import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { config } from "@/config";
import { SettingsApi } from "@/api/giftcard.api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X, Settings as SettingsIcon } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface SettingsData {
  currency: string;
  vat: string;
  communicationChannel: string;
  categories: string[];
}

const DEFAULT_SETTINGS: SettingsData = {
  currency: "",
  vat: "",
  communicationChannel: "",
  categories: [],
};

const GiftCardSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<SettingsData>(DEFAULT_SETTINGS);

  const [newCategory, setNewCategory] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [settingId, setSettingId] = useState<number>(0);
  const [malls, setMalls] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedMallId, setSelectedMallId] = useState<number | null>(null);
  const [isMallLoading, setIsMallLoading] = useState(false);
  const [isPhysicalCardAvailable, setIsPhysicalCardAvailable] =
    useState<boolean>(false);

  // Fetch settings on mount
  const fetchSettings = useCallback(async (mallIdParam: number) => {
    try {
      const userInfoRaw = sessionStorage.getItem("userInfo");
      const userId = userInfoRaw ? JSON.parse(userInfoRaw)?.userId ?? 0 : 0;

      const response = await axios.post(
        `${config.GIFT_API_BASE_URL}/Settings/GetSettings`,
        {
          p_Id: userId,
          p_MallId: mallIdParam,
        },
        {
          headers: {
            xapikey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      const data = response?.data?.setting;
      if (data) {
        setSettingId(data.id || 0);

        const parsedCategories = (data.categoryList || "")
          .split(",")
          .map((c: string) => c.trim())
          .filter((c: string) => c.length > 0);

        setSettings({
          currency: data.currency || "",
          vat:
            data.vatPercentage !== null && data.vatPercentage !== undefined
              ? String(data.vatPercentage)
              : "",
          communicationChannel: data.communicationChannel || "",
          categories: parsedCategories.length > 0 ? parsedCategories : [],
        });

        setIsPhysicalCardAvailable(data.isPhysicalCardAvailable ?? false);
      } else {
        setSettingId(0);
        setSettings(DEFAULT_SETTINGS);
        setIsPhysicalCardAvailable(false);
      }
    } catch (error) {
      setSettingId(0);
      setSettings(DEFAULT_SETTINGS);
      setIsPhysicalCardAvailable(false);
      toast({
        title: "Failed to load settings",
        description:
          "Could not fetch current settings. Please try again later.",
        variant: "destructive",
      });
    }
  }, []);

  const fetchMallOptions = useCallback(async () => {
    setIsMallLoading(true);
    try {
      const data = await SettingsApi.getMallDetails();
      const mallDetails = Array.isArray(data?.malldetails)
        ? data.malldetails
        : [];
      const mapped = mallDetails.map((mall: any) => ({
        id: mall.mallId,
        name: mall.mallName || `Mall ${mall.mallId}`,
      }));

      setMalls(mapped);
      if (mapped.length > 0) {
        setSelectedMallId(mapped[0].id);
      } else {
        setSelectedMallId(null);
      }
    } catch (error) {
      console.error("Error fetching malls:", error);
      setMalls([]);
      setSelectedMallId(null);
      toast({
        title: "Failed to load malls",
        description: "Could not fetch mall list. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsMallLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMallOptions();
  }, [fetchMallOptions]);

  useEffect(() => {
    if (selectedMallId !== null) {
      fetchSettings(selectedMallId);
    }
  }, [selectedMallId, fetchSettings]);

  const handleCurrencyChange = (value: string) => {
    setSettings((prev) => ({ ...prev, currency: value }));
  };

  const handleVatChange = (value: string) => {
    setSettings((prev) => ({ ...prev, vat: value }));
  };

  const handleCommunicationChannelChange = (value: string) => {
    setSettings((prev) => ({ ...prev, communicationChannel: value }));
  };

  const handleAddCategory = () => {
    if (
      newCategory.trim() &&
      !settings.categories.includes(newCategory.trim())
    ) {
      setSettings((prev) => ({
        ...prev,
        categories: [...prev.categories, newCategory.trim()],
      }));
      setNewCategory("");
      toast({
        title: "Category Added",
        description: `${newCategory.trim()} has been added to categories.`,
      });
    } else if (settings.categories.includes(newCategory.trim())) {
      toast({
        title: "Category Exists",
        description: "This category already exists.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveCategory = (categoryToRemove: string) => {
    setSettings((prev) => ({
      ...prev,
      categories: prev.categories.filter((cat) => cat !== categoryToRemove),
    }));
    toast({
      title: "Category Removed",
      description: `${categoryToRemove} has been removed from categories.`,
    });
  };

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true);

      const userInfoRaw = sessionStorage.getItem("userInfo");
      const parsedUserInfo = userInfoRaw ? JSON.parse(userInfoRaw) : null;
      const userId = parsedUserInfo?.userId ?? 0;
      const userName =
        parsedUserInfo?.username ||
        parsedUserInfo?.name ||
        parsedUserInfo?.userName ||
        "Admin";

      const categoryList = (settings.categories || [])
        .map((c) => c.trim())
        .filter((c) => c.length > 0)
        .join(",");

      const nowIso = new Date().toISOString();

      const payload = {
        p_Id: settingId,
        p_Currency: settings.currency || "",
        p_VATPercentage: settings.vat ? parseFloat(settings.vat) : 0,
        p_CategoryList: categoryList,
        p_MallId: selectedMallId ?? 0,
        p_CreatedBy: userId,
        p_CreatedDateTime: nowIso,
        p_UpdatedBy: userId,
        p_UpdatedDateTime: nowIso,
        p_CommunicationChannel: settings.communicationChannel || "",
        p_UserName: userName,
        p_IsPhysicalCardAvailable: isPhysicalCardAvailable,
      };

      await axios.post(
        `${config.GIFT_API_BASE_URL}/Settings/InsertUpdateSettings`,
        payload,
        {
          headers: {
            xapikey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      // Refresh settings after successful save
      try {
        if (selectedMallId !== null) {
          const refreshResp = await axios.post(
            `${config.GIFT_API_BASE_URL}/Settings/GetSettings`,
            {
              p_Id: userId,
              p_MallId: selectedMallId,
            },
            {
              headers: {
                xapikey: config.GIFT_API_KEY,
                "Content-Type": "application/json",
              },
            }
          );

          const data = refreshResp?.data?.setting;
          if (data) {
            setSettingId(data.id || 0);

            const parsedCategories = (data.categoryList || "")
              .split(",")
              .map((c: string) => c.trim())
              .filter((c: string) => c.length > 0);

            setSettings({
              currency: data.currency || "",
              vat:
                data.vatPercentage !== null && data.vatPercentage !== undefined
                  ? String(data.vatPercentage)
                  : "",
              communicationChannel: data.communicationChannel || "",
              categories: parsedCategories.length > 0 ? parsedCategories : [],
            });

            setIsPhysicalCardAvailable(data.isPhysicalCardAvailable ?? false);
          } else {
            setSettingId(0);
            setSettings(DEFAULT_SETTINGS);
            setIsPhysicalCardAvailable(false);
          }
        }
      } catch {}

      toast({
        title: "Settings Saved",
        description: "Your gift card settings have been saved successfully.",
      });

      // Navigate to gift card dashboard after successful save
      navigate("/giftcard");
    } catch (error) {
      toast({
        title: "Failed to save settings",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <SettingsIcon className="h-8 w-8 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600">
              Configure your gift card system settings
            </p>
          </div>
        </div>
        <div className="w-full lg:w-64">
          <Select
            value={
              selectedMallId !== null ? selectedMallId.toString() : undefined
            }
            onValueChange={(value) => setSelectedMallId(Number(value))}
            disabled={isMallLoading || malls.length === 0}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={isMallLoading ? "Loading malls..." : "Select mall"}
              />
            </SelectTrigger>
            <SelectContent className="!max-h-48 overflow-auto py-0">
              {malls.length > 0 ? (
                malls.map((mall) => (
                  <SelectItem key={mall.id} value={mall.id.toString()}>
                    {mall.name}
                  </SelectItem>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {isMallLoading ? "Loading..." : "No malls found"}
                </div>
              )}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">
            Settings are saved per mall.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Currency Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Currency Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="currency">Default Currency</Label>
              <Select
                value={settings.currency}
                onValueChange={handleCurrencyChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="AED">AED - UAE Dirham</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* VAT Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">VAT Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="vat">VAT Percentage</Label>
              <Input
                id="vat"
                type="number"
                placeholder="Enter VAT percentage"
                value={settings.vat}
                onChange={(e) => handleVatChange(e.target.value)}
                min="0"
                max="100"
                step="0.01"
              />
              <p className="text-sm text-gray-500 mt-1">
                Enter VAT percentage (e.g., 5 for 5%)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Communication Channel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Communication Channel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="communication">
                Default Communication Channel
              </Label>
              <Select
                value={settings.communicationChannel}
                onValueChange={handleCommunicationChannelChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select communication channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="Both">Both WhatsApp & Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Brand Category Management */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Brand Category Management</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="newCategory">Add New Category</Label>
              <div className="flex space-x-2">
                <Input
                  id="newCategory"
                  placeholder="Enter category name"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddCategory()}
                />
                <Button
                  onClick={handleAddCategory}
                  disabled={!newCategory.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label>Current Categories</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {settings.categories.map((category, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="flex items-center space-x-1"
                  >
                    <span>{category}</span>
                    <button
                      onClick={() => handleRemoveCategory(category)}
                      className="ml-1 hover:text-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              {settings.categories.length === 0 && (
                <p className="text-sm text-gray-500">No categories added yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Physical Card Support */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Physical Card Support</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="physicalCard"
                checked={isPhysicalCardAvailable}
                onCheckedChange={(checked) =>
                  setIsPhysicalCardAvailable(checked === true)
                }
              />
              <Label
                htmlFor="physicalCard"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Mall supports Physical Card
              </Label>
            </div>
            <p className="text-sm text-gray-500">
              Enable this option if the selected mall supports physical gift
              cards.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6">
        <Button
          onClick={handleSaveSettings}
          className="bg-amber-600 hover:bg-amber-700 text-white px-8"
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
};

export default GiftCardSettings;
